import { useState } from 'react';
import cn from 'classnames';
import { Form } from 'react-bootstrap';
import { MdAdd } from 'react-icons/md';
import { Button } from '@Components/Button';
import { Dropdown } from '@Components/Dropdown';
import { Checkbox } from '@Components/FormCheck';
import { IconButton } from '@Components/IconButton';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { Spinner } from '@Components/Spinner/Spinner';
import { Size } from '@Components/designConstants';
import { useSetError } from '@src/redux/modals';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import {
  AttributeInfo,
  AttributeType,
  StreamingNetworkRecord
} from './streaming-networks-constants';
import { buildRowKey, parseRowKey } from './streaming-networks-utils';
import {
  AttributeSchema,
  saveNetworkAttributes,
  useAttributeSchema
} from './use-streaming-networks';

interface NetworkAttributesTabProps {
  network: StreamingNetworkRecord;
  onRefetch: () => Promise<unknown>;
}

const DEFAULT_LABEL = '(default)';

const displayRowKeyParts = (rowKey: string): string => {
  const { platform, description, property } = parseRowKey(rowKey);
  const parts = [platform || DEFAULT_LABEL, description || DEFAULT_LABEL];
  if (property !== undefined) {
    parts.push(property || DEFAULT_LABEL);
  }
  return parts.join(' / ');
};

export const NetworkAttributesTab = ({ network, onRefetch }: NetworkAttributesTabProps) => {
  const attributeSchemaQuery = useAttributeSchema();
  const setError = useSetError();

  const [addedRowKeys, setAddedRowKeys] = useState<string[]>([]);
  const [selectedRow, setSelectedRow] = useState<string>();
  const [localChanges, setLocalChanges] = useState<Record<string, AttributeInfo>>({});
  const [saving, setSaving] = useState(false);

  const existingRowKeys = Object.keys(network.attributes ?? {});
  const rowKeys = [
    ...existingRowKeys,
    ...addedRowKeys.filter(key => !existingRowKeys.includes(key))
  ];

  const selectRow = (rowKey: string) => {
    setSelectedRow(rowKey);
    setLocalChanges({});
  };

  const addRow = (rowKey: string) => {
    if (!rowKeys.includes(rowKey)) {
      setAddedRowKeys(prev => [...prev, rowKey]);
    }
    selectRow(rowKey);
  };

  const discardChanges = () => setLocalChanges({});

  const handleSave = async () => {
    if (!selectedRow) return;
    const { platform, description, property } = parseRowKey(selectedRow);
    const propertyRecord = property
      ? network.properties.find(p => p.propertyShortCode === property)
      : undefined;
    setSaving(true);
    try {
      await saveNetworkAttributes({
        networkInfo: {
          network: propertyRecord ? propertyRecord.propertyShortCode : network.shortCode,
          platform,
          description
        },
        edits: localChanges
      });
      await onRefetch();
      setLocalChanges({});
      Mixpanel.track(MxE.RecordSaved, {
        page: PAGE_NAMES.STREAMING_NETWORKS,
        entity: 'network_attributes'
      });
    } catch (e) {
      setError({ message: `Save failed: ${e.message}`, reportError: e });
    }
    setSaving(false);
  };

  if (attributeSchemaQuery.isLoading) {
    return <Spinner />;
  }

  const schema = attributeSchemaQuery.data ?? {};
  const combinedData: Record<string, AttributeInfo | undefined> = {
    ...(selectedRow ? network.attributes?.[selectedRow] : undefined),
    ...localChanges
  };

  const setAttribute = (name: string, value: AttributeInfo) =>
    setLocalChanges(prev => ({ ...prev, [name]: value }));

  return (
    <div className='sn-attributes'>
      <AttributeRowList
        network={network}
        rowKeys={rowKeys}
        selectedRow={selectedRow}
        onSelect={selectRow}
        onAddRow={addRow}
      />
      <AttributeEditorPane
        selectedRow={selectedRow}
        schema={schema}
        combinedData={combinedData}
        localChanges={localChanges}
        saving={saving}
        onSetAttribute={setAttribute}
        onDiscard={discardChanges}
        onSave={handleSave}
      />
    </div>
  );
};

// ---- left pane: row list + "add row" composer ----

interface AttributeRowListProps {
  network: StreamingNetworkRecord;
  rowKeys: string[];
  selectedRow?: string;
  onSelect: (rowKey: string) => void;
  onAddRow: (rowKey: string) => void;
}

const AttributeRowList = ({
  network,
  rowKeys,
  selectedRow,
  onSelect,
  onAddRow
}: AttributeRowListProps) => {
  const [composing, setComposing] = useState(false);

  const handleAdd = (rowKey: string) => {
    onAddRow(rowKey);
    setComposing(false);
  };

  return (
    <div className='sn-attributes-list'>
      <div className='sn-attributes-list-head'>
        <span className='sn-attributes-list-title'>Rows</span>
        <IconButton size={Size.MD} aria-label='Add row' onClick={() => setComposing(true)}>
          <MdAdd />
        </IconButton>
      </div>
      {composing && (
        <NewRowForm network={network} onConfirm={handleAdd} onCancel={() => setComposing(false)} />
      )}
      {rowKeys.length === 0 && !composing ? (
        <div className='sn-empty'>No rows yet.</div>
      ) : (
        rowKeys.map(rowKey => (
          <InteractiveDiv
            key={rowKey}
            className={cn('sn-attributes-row', { active: rowKey === selectedRow })}
            onClick={() => onSelect(rowKey)}
          >
            {displayRowKeyParts(rowKey)}
          </InteractiveDiv>
        ))
      )}
    </div>
  );
};

interface NewRowFormProps {
  network: StreamingNetworkRecord;
  onConfirm: (rowKey: string) => void;
  onCancel: () => void;
}

const NewRowForm = ({ network, onConfirm, onCancel }: NewRowFormProps) => {
  const [platform, setPlatform] = useState('');
  const [description, setDescription] = useState('');
  const [property, setProperty] = useState('');
  const hasProperties = network.properties.length > 0;

  // The network's real, verified Content list (same data the Content tab manages) is the source
  // of truth here, not a free-text concept.
  const contentOptions = [
    ...new Set(
      network.globalContent.filter(content => !content.deleted).map(content => content.contentValue)
    )
  ];

  const confirm = () =>
    onConfirm(buildRowKey(platform, description, hasProperties ? property : undefined));

  return (
    <div className='sn-attributes-add-form'>
      <Dropdown<string>
        options={network.platforms.map(p => p.platform)}
        value={platform || undefined}
        placeholder='Platform'
        onChange={setPlatform}
      />
      <Dropdown<string>
        searchable
        clearable
        options={contentOptions}
        value={description || undefined}
        placeholder='Description'
        onChange={value => setDescription(value ?? '')}
        onClear={() => setDescription('')}
      />
      {hasProperties && (
        <Dropdown<string>
          options={network.properties.map(p => ({
            label: p.propertyName,
            value: p.propertyShortCode
          }))}
          value={property || undefined}
          placeholder='Property'
          onChange={setProperty}
        />
      )}
      <div className='sn-attributes-add-form-actions'>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={confirm}>Add</Button>
      </div>
    </div>
  );
};

// ---- right pane: attribute editor for the selected row ----

interface AttributeEditorPaneProps {
  selectedRow?: string;
  schema: Record<string, AttributeSchema>;
  combinedData: Record<string, AttributeInfo | undefined>;
  localChanges: Record<string, AttributeInfo>;
  saving: boolean;
  onSetAttribute: (name: string, value: AttributeInfo) => void;
  onDiscard: () => void;
  onSave: () => Promise<void>;
}

const AttributeEditorPane = ({
  selectedRow,
  schema,
  combinedData,
  localChanges,
  saving,
  onSetAttribute,
  onDiscard,
  onSave
}: AttributeEditorPaneProps) => {
  if (!selectedRow) {
    return (
      <div className='sn-attributes-editor sn-empty'>Select a row on the left to edit metadata</div>
    );
  }

  const attributeNames = Object.keys(combinedData).filter(name => combinedData[name] !== undefined);
  const hasPendingChanges = Object.keys(localChanges).length > 0;

  return (
    <div className='sn-attributes-editor'>
      <div className='sn-attributes-cards'>
        {attributeNames.map(name => {
          const catalogEntry = schema[name];
          const attributeInfo = combinedData[name];
          if (!attributeInfo) return undefined;
          if (attributeInfo.type === AttributeType.BOOLEAN) {
            const base = catalogEntry ?? attributeInfo;
            return (
              <Checkbox
                key={name}
                className='sn-attributes-boolean'
                label={name}
                checked={Boolean(attributeInfo.value)}
                onChange={event => onSetAttribute(name, { ...base, value: event.target.checked })}
              />
            );
          }
          return (
            <div
              className={cn('sn-attributes-card', {
                'sn-attributes-card-wide':
                  attributeInfo.type === AttributeType.MULTI_SELECT ||
                  attributeInfo.type === AttributeType.TEXT
              })}
              key={name}
            >
              <span className='sn-attributes-card-label'>{name}</span>
              <AttributeValueControl
                attributeInfo={attributeInfo}
                catalogEntry={catalogEntry}
                onChange={value => onSetAttribute(name, value)}
              />
            </div>
          );
        })}
      </div>
      <AddAttributeControl schema={schema} existingNames={attributeNames} onAdd={onSetAttribute} />
      {hasPendingChanges && (
        <div className='sn-attributes-editor-actions'>
          <Button disabled={saving} onClick={onDiscard}>
            Discard
          </Button>
          <Button loading={saving} onClick={onSave}>
            Save
          </Button>
        </div>
      )}
    </div>
  );
};

interface AttributeValueControlProps {
  attributeInfo: AttributeInfo;
  catalogEntry?: AttributeSchema;
  onChange: (value: AttributeInfo) => void;
}

const AttributeValueControl = ({
  attributeInfo,
  catalogEntry,
  onChange
}: AttributeValueControlProps) => {
  const base = catalogEntry ?? attributeInfo;

  if (attributeInfo.type === AttributeType.MULTI_SELECT) {
    const options = Array.isArray(catalogEntry?.value) ? catalogEntry.value : [];
    const value = Array.isArray(attributeInfo.value) ? attributeInfo.value : [];
    return (
      <Dropdown<string>
        multiSelect
        searchable
        showChips
        options={options}
        value={value}
        onChange={newValue => onChange({ ...base, value: newValue })}
      />
    );
  }

  if (attributeInfo.type === AttributeType.TEXT) {
    return (
      <Form.Control
        as='textarea'
        size='sm'
        rows={4}
        value={String(attributeInfo.value ?? '')}
        onChange={event => onChange({ ...base, value: event.target.value })}
      />
    );
  }

  return (
    <Form.Control
      size='sm'
      value={String(attributeInfo.value ?? '')}
      onChange={event => onChange({ ...base, value: event.target.value })}
    />
  );
};

interface AddAttributeControlProps {
  schema: Record<string, AttributeSchema>;
  existingNames: string[];
  onAdd: (name: string, value: AttributeInfo) => void;
}

const AddAttributeControl = ({ schema, existingNames, onAdd }: AddAttributeControlProps) => {
  const [newAttributeName, setNewAttributeName] = useState<string>();
  const availableNames = Object.keys(schema).filter(name => !existingNames.includes(name));

  const confirmAdd = () => {
    if (!newAttributeName) return;
    const catalogEntry = schema[newAttributeName];
    if (!catalogEntry) return;
    const defaultValue = catalogEntry.type === AttributeType.BOOLEAN ? true : catalogEntry.value;
    onAdd(newAttributeName, { ...catalogEntry, value: defaultValue });
    setNewAttributeName(undefined);
  };

  if (availableNames.length === 0) return undefined;

  return (
    <div className='sn-attributes-add-attribute'>
      <div className='sn-attributes-add-attribute-dropdown'>
        <Dropdown<string>
          searchable
          options={availableNames}
          value={newAttributeName}
          placeholder='Add an attribute…'
          onChange={value => setNewAttributeName(value ?? undefined)}
        />
      </div>
      <IconButton
        size={Size.MD}
        aria-label='Add attribute'
        disabled={!newAttributeName}
        onClick={confirmAdd}
      >
        <MdAdd />
      </IconButton>
    </div>
  );
};
